import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import {
  getUniversities,
  patchUniversity,
} from "../../../services/Universities/universitiesService";
import { getUniversityTypes } from "../../../services/Universities/universityTypesService";
import { UserContext } from "../../../utils/contexts/UserContext";
import { useForm, Controller } from "react-hook-form";
import TextField from "../../../components/forms/TextField/TextField";
import SmashLoader from "../../../components/Loader/SmashLoader";
import { toast } from "react-toastify";
import Select from "../../../components/forms/Select/Select";
import NotFound from "../../NotFound/NotFound";

export default function UniversityEdit({ organizationUuid }) {
  const { isAdmin } = useContext(UserContext);
  const navigate = useNavigate();
  const { uuid: routeUuid } = useParams();
  const uuid = organizationUuid || routeUuid;

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const [university, setUniversity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [universityTypes, setUniversityTypes] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    const reqs = [
      getUniversities({ params: { uuid }, signal: controller.signal }),
      getUniversityTypes({ signal: controller.signal }),
    ];
    Promise.all(reqs)
      .then((res) => {
        setUniversity(res[0]);
        setUniversityTypes(
          res[1].results.map((type) => ({
            label: type.name,
            value: type.uuid,
          }))
        );
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });

    return () => controller.abort();
  }, [uuid]);
  const handleCancel = (e) => {
    // setUniversity({ ...university, [e.target.name]: e.target.value });
    navigate(organizationUuid ? "/organization/dashboard" : `/universities/${uuid}`);
  };

  const onSubmit = (data) => {
    patchUniversity(uuid, data)
      .then((res) => {
        setUniversity(res);
        toast.success("Organization updated successfully");
      })
      .catch((err) => {
        toast.error("Failed to update organization");
      });
  };

  useEffect(() => {
    console.log(errors);
  }, [errors]);
  if (loading) {
    return <SmashLoader />;
  }

  if (!university) {
    return <NotFound />;
  }

  return (
    <div
      className="min-h-screen font-inter transition-colors duration-300
      bg-gray-100 text-gray-900 dark:bg-gray-900 dark:text-white"
    >
      <div className="max-w-7xl mx-auto py-8 px-4 md:px-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 md:p-10">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="mb-4">
              <TextField
                label="Organization Name"
                {...register("name", {
                  value: university.name,
                })}
                error={errors.name?.message}
              />
            </div>

            <div className="mb-4">
              <TextField
                label="Short Name"
                {...register("short_name", {
                  required: "Short name is required",
                  value: university.short_name,
                })}
                error={errors.short_name?.message}
              />
            </div>

            {/* Email */}
            <div className="mb-4">
              <TextField
                label="Email"
                type="email"
                {...register("email", {
                  value: university.email,
                  required: "Email is required",
                })}
                error={errors.email?.message}
              />
            </div>

            {/* Country */}
            <div className="mb-4">
              <TextField
                label="Country"
                {...register("country", {
                  required: "Country is required",
                  value: university?.country,
                })}
                error={errors.country?.message}
              />
            </div>

            {/* State */}
            <div className="mb-4">
              <TextField
                label="State"
                {...register("state", {
                  required: "State is required",
                  value: university?.state,
                })}
                error={errors.state?.message}
              />
            </div>

            {/* City */}
            <div className="mb-4">
              <TextField
                label="City"
                {...register("city", {
                  required: "City is required",
                  value: university?.city,
                })}
                error={errors.city?.message}
              />
            </div>

            {/* Address */}
            <div className="mb-4">
              <TextField
                label="Address"
                {...register("address", {
                  required: "Address is required",
                  value: university?.address,
                })}
                error={errors.address?.message}
              />
            </div>

            {/* Postal Code */}
            <div className="mb-4">
              <TextField
                label="Postal Code"
                {...register("postal_code", {
                  required: "Postal code is required",
                  value: university?.postal_code,
                })}
                error={errors.postal_code?.message}
              />
            </div>

            {/* Website */}
            <div className="mb-4">
              <TextField
                label="Website"
                type="url"
                {...register("website", {
                  required: "Website is required",
                  value: university?.website,
                })}
                error={errors.website?.message}
              />
            </div>

            {/* Founded Year */}
            <div className="mb-4">
              <TextField
                label="Founded Year"
                type="number"
                {...register("founded_year", {
                  required: "Founded year is required",
                  value: university?.founded_year,
                })}
                error={errors.founded_year?.message}
              />
            </div>

            {/* Ranking */}
            {isAdmin && (
              <div className="mb-4">
                <TextField
                  label="World Ranking (Optional)"
                  type="number"
                  {...register("ranking", {
                    value: university?.ranking,
                  })}
                  error={errors.ranking?.message}
                />
              </div>
            )}

            {/* AI follow-up limits are read-only here: only a platform
                administrator changes them, on /admin/organizations. */}
            {university?.followup_time_budget_seconds !== undefined && (
              <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
                AI follow-up limits:{" "}
                <strong>{university.followup_time_budget_seconds}s</strong> of
                answer time, up to{" "}
                <strong>{university.followup_max_count}</strong> follow-up
                questions per question. Contact a platform administrator to
                change them.
              </p>
            )}

            {/* Organization type */}
            <div className="mb-4">
              <Controller
                control={control}
                name="type"
                defaultValue={university?.type?.uuid}
                render={({ field }) => (
                  <Select
                    label="Organization Type"
                    options={universityTypes}
                    value={field.value}
                    onChange={(v) => field.onChange(v.value)}
                    error={errors.type?.message}
                  />
                )}
              />
            </div>

            {/* Description */}
            <div className="space-y-4 mb-4">
              {/* Bio */}
              <div className="flex flex-col">
                <label className="text-sm mb-1 font-semibold text-gray-900 dark:text-gray-200">
                  Description
                </label>
                <textarea
                  {...register("description", {
                    value: university.description || "",
                  })}
                  placeholder="Describe the organization..."
                  className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:text-white dark:border-gray-600 resize-none"
                  rows={4}
                />
              </div>
            </div>
            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleCancel}
                className="py-3 px-6 rounded-lg font-semibold transition duration-300 ease-in-out
                  bg-gray-300 hover:bg-gray-400 text-gray-800 shadow-md
                  dark:bg-gray-600 dark:hover:bg-gray-700 dark:text-gray-200
                  focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-75"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="py-3 px-6 rounded-lg font-semibold transition duration-300 ease-in-out
                  bg-blue-600 hover:bg-blue-700 text-white shadow-md
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75"
              >
                Save Changes
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
